/**
 * brand/config.ts
 *
 * Default platform brand config — derived from the platform logo.
 *
 * Color palette (extracted from logo):
 *   Primary  — Orange  #F56200  (building shell, door, calendar highlight)
 *   Accent   — Green   #2BA84A  (sparkle star, checkmark badge)
 *   Neutral  — Near-black through white
 *
 * This is the ONLY file that may contain raw color hex values.
 * All other files reference BrandConfig through useBrand() or CSS variables.
 *
 * Tenant overrides are applied in BrandProvider by deep-merging a partial
 * BrandConfig stored in tenant_settings.brand_config_json.
 */

import type { BrandConfig } from './types'

export const DEFAULT_BRAND: BrandConfig = {
  name: 'ReadyRoomOps',

  colors: {
    // ── Primary (orange) ──────────────────────────────────────────────────
    primary:       '#F56200',
    primaryHover:  '#D95300',   // -12% lightness
    primarySubtle: '#FEF0E6',   // 10% opacity equivalent on white

    // ── Accent (green) ────────────────────────────────────────────────────
    accent:        '#2BA84A',
    accentHover:   '#22883C',
    accentSubtle:  '#E8F7EC',

    // ── Danger ────────────────────────────────────────────────────────────
    danger:        '#D93025',
    dangerHover:   '#B32219',
    dangerSubtle:  '#FDECEA',

    // ── Warning ───────────────────────────────────────────────────────────
    warning:       '#E8A000',
    warningSubtle: '#FEF7E0',

    // ── Neutrals ──────────────────────────────────────────────────────────
    neutral900:    '#111827',
    neutral700:    '#374151',
    neutral500:    '#6B7280',
    neutral300:    '#D1D5DB',
    neutral100:    '#F3F4F6',
    neutral50:     '#F9FAFB',
    white:         '#FFFFFF',
  },

  typography: {
    fontFamily:        "'Inter', 'system-ui', sans-serif",
    fontFamilyDisplay: "'Inter', 'system-ui', sans-serif",
    fontSizeBase:      '1rem',
  },

  shape: {
    radiusSm:   '0.25rem',
    radiusMd:   '0.5rem',
    radiusLg:   '0.75rem',
    radiusFull: '9999px',
  },

  assets: {
    // The platform logo is stored in Supabase Storage under the platform org.
    // Replace this URL after first deploy with the actual signed/public URL.
    logoUrl:     '/brand/logo.png',
    logoMarkUrl: '/brand/logo.png',
    faviconUrl:  '/brand/favicon.ico',
  },
}
